// Globals
import { Button, Checkbox, GetProps } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';

// Modules
import { useSocket } from '@/modules/Socket';
import { useSettings } from '@/modules/Settings';

// Components
import { ClearLogoIcon, CloseOutlineIcon, LogoIcon } from '@/icons';
import { BubbleHeader, ComparisonTable, Message, ProductCards } from '../../components';
import { SenderContainer } from '../SenderContainer/SenderContainer';
import {
  BotName,
  ContentScrollArea,
  DebugCheckboxLabel,
  HeaderAvatar,
  HeaderBar,
  HeaderIconButton,
  HeaderInfo,
  SenderStickyFooter,
  StatusDot,
  StatusRow,
  StyledBotNote,
  StyledBubbleList,
  StyledContainer,
  StyledExtra,
  StyledTime,
  StyledWrapper,
} from './ChatContainerStyles';

// Helpers
import { buildSystemBubbleItem, hasBubbleHeader } from '../../helpers';
import { DEBUG_TOOLS_ENABLED, DEFAULT_AGENT_NAME, DEFAULT_MAIN_COLOR } from '@/constants/common';
import { formatDate, formatTime } from '@/utils';

type TChatContainerWrapperProps = {
  onClose: () => void;
}

type BubbleDataType = NonNullable<GetProps<typeof StyledBubbleList>['items']>[number];

const ChatContainerWrapper = ({ onClose }: TChatContainerWrapperProps) => {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const {
    sendMessage,
    messages,
    isTyping,
    isConversationClosed,
    restartSession,
    isDebugEnabled,
    setDebugEnabled,
  } = useSocket();
  const { settings } = useSettings();
  const mainColor = settings?.main_color ?? DEFAULT_MAIN_COLOR;
  const senderName = settings?.sender_name ?? DEFAULT_AGENT_NAME;
  const disclaimerMessage = settings?.disclaimer_message
    ?? `${senderName} can make mistakes — verify part numbers before ordering.`;

  useEffect(() => {
    (messagesEndRef.current as HTMLElement)?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSendMessage = useCallback((message: string) => {
    sendMessage(message);

    setTimeout(() => {
      (messagesEndRef.current as HTMLElement)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [sendMessage]);

  const items = useMemo(() => {
    const items: BubbleDataType[] = messages.map((item, index) => {
      const key = item.seq ? `seq-${item.seq}` : `idx-${index}`;

      if (item.sender === 'SYSTEM') return buildSystemBubbleItem(item, key);

      return {
        key,
        content: (
          <>
            {item.message && <Message message={item.message.toString()} />}
            {item.cards?.length ? <ProductCards items={item.cards} primaryColor={mainColor} /> : null}
            {item.comparisonData && <ComparisonTable data={item.comparisonData} primaryColor={mainColor} />}
          </>
        ),
        placement: item.sender === 'USER' ? 'end' as const : 'start' as const,
        loading: false,
        header: hasBubbleHeader(item.sender)
          ? <BubbleHeader sender={item.sender} label={item.label} senderName={senderName} mainColor={mainColor} />
          : undefined,
        footer: item.date && <StyledTime dateTime={formatDate(item.date)}>{formatTime(item.date)}</StyledTime>
      };
    });

    return isTyping ? items.concat([{
      key: 'isTyping',
      content: '...' as const,
      loading: true,
      placement: 'start' as const
    }]) : items;
  }, [messages, isTyping, mainColor, senderName]);

  return (
    <StyledWrapper>
      <StyledContainer
        $primary={mainColor}
        title={
          <HeaderBar $primary={mainColor}>
            <HeaderAvatar $primary={mainColor}>
              <LogoIcon />
            </HeaderAvatar>
            <HeaderInfo>
              <BotName>{senderName}</BotName>
              <StatusRow>
                <StatusDot />
                <span>Online</span>
              </StatusRow>
            </HeaderInfo>
          </HeaderBar>
        }
        type="inner"
        extra={
          <StyledExtra>
            {DEBUG_TOOLS_ENABLED && (
              <DebugCheckboxLabel>
                <Checkbox
                  checked={isDebugEnabled}
                  onChange={(event) => setDebugEnabled(event.target.checked)}
                />
                Debug
              </DebugCheckboxLabel>
            )}
            <HeaderIconButton>
              <Button size="large" ghost icon={<ClearLogoIcon />} onClick={restartSession} />
            </HeaderIconButton>
            <HeaderIconButton>
              <Button size="large" ghost icon={<CloseOutlineIcon />} onClick={onClose} />
            </HeaderIconButton>
          </StyledExtra>
        }
      >
        <ContentScrollArea>
          <StyledBubbleList items={items} />
          <div ref={messagesEndRef} />
        </ContentScrollArea>

        <SenderStickyFooter>
          <SenderContainer onSend={handleSendMessage} disabled={isTyping || isConversationClosed} />
          <StyledBotNote>
            {disclaimerMessage}
          </StyledBotNote>
        </SenderStickyFooter>
      </StyledContainer >
    </StyledWrapper>
  );
};

export default ChatContainerWrapper;
